import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Divider} from 'primereact/divider'
import {Fragment, useCallback, useEffect, useMemo, useRef} from 'react'
import {ReactComponent as DuoUserHeart} from '../../../assets/icons/DuoUserHeart.svg'
import {useAppConfig} from '../../../context/app/AppContext'
import {useAuth} from '../../authentication/AuthProvider'
import NextOfKinForm from '../../next-of-kin/NextOfKinForm'
import {usePageNextOfKin} from '../../next-of-kin/query-keys/NextOfKinKeysManager'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {usePatientContext} from '../information/StyleDetailInformationWrapper'
import StyledCardListWrapper from '../StyledCardListWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'

const RelevantContactCard = () => {
    useSignals()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentViewedUser} = usePatientContext()
    // const {currentHospital} = useUserHospital()
    const {patientManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.relevantContact,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(
                tableTypes.relevantContact,
            )
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    const filterRequest = useSignal({
        globalSearchValue: null,
        userId: currentViewedUser?.id,
        ...initialAdvancedFilterRequest,
    })

    const {data: nokDataList, isFetched} = usePageNextOfKin(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        {
            ...filterRequest.value,
            userId: currentViewedUser?.id,
        },
        !!user?.id,
    )
    const cardList = useSignal([])

    useEffect(() => {
        if (nokDataList && nokDataList.content != cardList.value) {
            cardList.value = nokDataList.content
        }
    }, [nokDataList])

    const formRef = useRef(null)

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])

    const handleAction = rowValue => {
        formRef.current?.showForm({...rowValue})
    }

    const template = item => {
        return (
            <div
                key={item.id}
                className="h-10rem w-full mt-2"
                onClick={() => handleAction(item)}>
                {/* Header */}

                <div className="flex  justify-content-between">
                    <div className="flex gap-2">
                        <h5 className="text-700 uppercase font-bold  m-0 mb-1">
                            {item?.fullName || 'N/A'}
                        </h5>
                        <div>
                            {item?.relationship && (
                                <span className="text-xs text-white font-medium bg-blue-600 px-1 py-1 border-round">
                                    {item?.relationshipValue}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="w-3rem h-3rem bg-white  flex align-items-center justify-content-center">
                        <DuoUserHeart className="text-blue-500" />
                    </div>
                </div>

                <Divider className="p-2 m-0" type="dashed" />
                <div className="space-y-2 truncate">
                    {item?.email && (
                        <div className="flex align-items-center gap-2 text-sm mb-1">
                            <i className="pi pi-envelope text-gray-900 text-xs w-1rem"></i>
                            <span className="text-700 truncate">
                                {item?.email ? item.email : 'n/a'}
                            </span>
                        </div>
                    )}

                    <div className="flex align-items-center gap-2 text-sm  mb-1">
                        <i className="pi pi-phone text-gray-900 text-xs w-1rem"></i>
                        <span className="text-700">
                            {item?.phone ? item.phone : 'n/a'}
                        </span>
                    </div>

                    <div className="flex align-items-start gap-2 text-sm">
                        <i className="pi pi-map-marker text-gray-900 text-xs w-1rem mt-1"></i>
                        <span className="text-700 line-height-3">
                            {item?.address ? item.address : 'n/a'}
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            {/* Minimal Header */}
            <StyledCardListWrapper
                isDelete={false}
                isEdit={true}
                handleAddNew={handleAddNew}
                initialData={cardList.value}
                template={template}
                handleAction={handleAction}
            />
            <NextOfKinForm ref={formRef} />
        </Fragment>
    )
}

export default RelevantContactCard
